import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { dateTime, money } from "../lib/format";
import { ui } from "../lib/ui";

interface Cliente {
  id: number;
  documento?: string | null;
  tipo_documento: string;
  razon_social: string;
  condicion_iva: string;
  direccion?: string | null;
  telefono?: string | null;
  email?: string | null;
  limite_credito: number;
  saldo_cta_cte: number;
  activo: boolean;
}

interface Movimiento {
  id: number;
  fecha: string;
  tipo: string;
  debe: number;
  haber: number;
  saldo: number;
  comprobante_ref?: string | null;
  observaciones?: string | null;
}

const emptyForm: Partial<Cliente> = {
  documento: "",
  tipo_documento: "DNI",
  razon_social: "",
  condicion_iva: "CF",
  direccion: "",
  telefono: "",
  email: "",
  limite_credito: 0,
  activo: true,
};

export default function Clientes() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [creating, setCreating] = useState(false);
  const [cobrandoA, setCobrandoA] = useState<Cliente | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["clientes", debounced],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit: 200 };
      if (debounced) params.q = debounced;
      const { data } = await api.get<Cliente[]>("/api/customers", { params });
      return data;
    },
  });

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Clientes</h1>
          <p className={ui.subtitle}>Base de clientes y cuenta corriente</p>
        </div>
        <button className={ui.btnPrimary} onClick={() => setCreating(true)}>
          + Nuevo cliente
        </button>
      </header>

      <div className={ui.page}>
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            placeholder="Buscar por nombre o documento..."
            className={ui.input + " max-w-md"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && <div className={ui.error}>Error al cargar clientes.</div>}

        <div className={ui.card}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>Documento</th>
                <th className={ui.th}>Razón social</th>
                <th className={ui.th}>Cond. IVA</th>
                <th className={ui.th + " text-right"}>Saldo cta cte</th>
                <th className={ui.th + " text-center"}>Activo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td colSpan={5} className="p-3">
                      <div className={ui.skeleton + " h-6 w-full"} />
                    </td>
                  </tr>
                ))}
              {!isLoading &&
                data?.map((c) => {
                  const saldo = Number(c.saldo_cta_cte);
                  return (
                    <tr
                      key={c.id}
                      className={ui.tr}
                      onClick={() => setEditing(c)}
                    >
                      <td className={ui.td + " font-mono text-xs"}>
                        {c.tipo_documento} {c.documento || "—"}
                      </td>
                      <td className={ui.td + " font-medium text-slate-900"}>
                        {c.razon_social}
                      </td>
                      <td className={ui.td}>{c.condicion_iva}</td>
                      <td
                        className={
                          ui.td +
                          " text-right font-semibold " +
                          (saldo > 0 ? "text-rose-600" : "text-slate-400")
                        }
                      >
                        {money(saldo)}
                      </td>
                      <td className={ui.td + " text-center"}>
                        <span
                          className={
                            ui.badge +
                            " " +
                            (c.activo
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-500")
                          }
                        >
                          {c.activo ? "Sí" : "No"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && !data?.length && (
                <tr>
                  <td colSpan={5} className={ui.empty}>
                    Sin clientes. Creá el primero con "+ Nuevo cliente".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(creating || editing) && (
        <ClienteModal
          cliente={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["clientes"] });
            setCreating(false);
            setEditing(null);
          }}
          onCobrar={(c) => {
            setEditing(null);
            setCobrandoA(c);
          }}
        />
      )}
      {cobrandoA && (
        <CobroModal
          cliente={cobrandoA}
          onClose={() => setCobrandoA(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["clientes"] });
            setCobrandoA(null);
          }}
        />
      )}
    </div>
  );
}

function ClienteModal({
  cliente,
  onClose,
  onSaved,
  onCobrar,
}: {
  cliente: Cliente | null;
  onClose: () => void;
  onSaved: () => void;
  onCobrar: (c: Cliente) => void;
}) {
  const [form, setForm] = useState<Partial<Cliente>>(() =>
    cliente ? { ...cliente } : { ...emptyForm }
  );
  const isEdit = !!cliente;

  const { data: movs } = useQuery({
    queryKey: ["cta-cte", "cliente", cliente?.id],
    queryFn: async () => {
      const { data } = await api.get<Movimiento[]>(
        `/api/cta-cte/clientes/${cliente!.id}/movimientos`,
        { params: { limit: 10 } }
      );
      return data;
    },
    enabled: !!cliente,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const { id: _id, saldo_cta_cte: _s, ...rest } = form as Cliente;
        await api.patch(`/api/customers/${cliente!.id}`, rest);
      } else {
        await api.post("/api/customers", form);
      }
    },
    onSuccess: onSaved,
  });

  const update = (k: keyof Cliente, v: unknown) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className={ui.modalOverlay} onClick={onClose}>
      <div className={ui.modalCardLg} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900">
            {isEdit ? "Editar cliente" : "Nuevo cliente"}
          </h2>
          {isEdit && (
            <button
              className={ui.btnPrimary + " py-2 px-3 text-sm"}
              onClick={() => onCobrar(cliente!)}
            >
              + Registrar cobro
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={ui.label}>Tipo doc</label>
            <select
              className={ui.input}
              value={form.tipo_documento ?? "DNI"}
              onChange={(e) => update("tipo_documento", e.target.value)}
            >
              <option value="DNI">DNI</option>
              <option value="CUIT">CUIT</option>
              <option value="CUIL">CUIL</option>
              <option value="SD">Sin doc</option>
            </select>
          </div>
          <div>
            <label className={ui.label}>Documento</label>
            <input
              className={ui.input}
              value={form.documento ?? ""}
              onChange={(e) => update("documento", e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className={ui.label}>Razón social</label>
            <input
              className={ui.input}
              value={form.razon_social ?? ""}
              onChange={(e) => update("razon_social", e.target.value)}
            />
          </div>
          <div>
            <label className={ui.label}>Cond. IVA</label>
            <select
              className={ui.input}
              value={form.condicion_iva ?? "CF"}
              onChange={(e) => update("condicion_iva", e.target.value)}
            >
              <option value="CF">Consumidor Final</option>
              <option value="RI">Responsable Inscripto</option>
              <option value="MT">Monotributo</option>
              <option value="EX">Exento</option>
            </select>
          </div>
          <div>
            <label className={ui.label}>Límite crédito</label>
            <input
              type="number"
              className={ui.input}
              value={Number(form.limite_credito ?? 0)}
              onChange={(e) =>
                update("limite_credito", parseFloat(e.target.value) || 0)
              }
            />
          </div>
          <div>
            <label className={ui.label}>Teléfono</label>
            <input
              className={ui.input}
              value={form.telefono ?? ""}
              onChange={(e) => update("telefono", e.target.value)}
            />
          </div>
          <div>
            <label className={ui.label}>Email</label>
            <input
              className={ui.input}
              value={form.email ?? ""}
              onChange={(e) => update("email", e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className={ui.label}>Dirección</label>
            <input
              className={ui.input}
              value={form.direccion ?? ""}
              onChange={(e) => update("direccion", e.target.value)}
            />
          </div>
          <div className="col-span-2 flex items-center gap-2 pt-1">
            <input
              id="activo-cli"
              type="checkbox"
              checked={!!form.activo}
              onChange={(e) => update("activo", e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
            />
            <label htmlFor="activo-cli" className="text-sm text-slate-700">
              Cliente activo
            </label>
          </div>
        </div>

        {isEdit && (
          <div className="mt-6">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
              Cuenta corriente — últimos movimientos
            </h3>
            <div className="bg-slate-50 rounded-lg p-3 max-h-60 overflow-y-auto">
              {!movs?.length ? (
                <p className="text-sm text-slate-400 text-center py-4">
                  Sin movimientos.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left py-1">Fecha</th>
                      <th className="text-left">Tipo</th>
                      <th className="text-right">Debe</th>
                      <th className="text-right">Haber</th>
                      <th className="text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movs.map((m) => (
                      <tr key={m.id} className="border-t border-slate-200">
                        <td className="py-1">{dateTime(m.fecha)}</td>
                        <td>{m.tipo}</td>
                        <td className="text-right">{money(m.debe)}</td>
                        <td className="text-right">{money(m.haber)}</td>
                        <td className="text-right font-semibold">
                          {money(m.saldo)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {save.isError && (
          <div className={ui.error + " mt-4"}>Error guardando cliente.</div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button className={ui.btnSecondary} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={ui.btnPrimary}
            disabled={save.isPending || !form.razon_social}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CobroModal({
  cliente,
  onClose,
  onSaved,
}: {
  cliente: Cliente;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [monto, setMonto] = useState<number>(0);
  const [medio, setMedio] = useState("EFECTIVO");
  const [ref, setRef] = useState("");
  const [obs, setObs] = useState("");

  const m = useMutation({
    mutationFn: async () => {
      await api.post("/api/cta-cte/clientes/cobro", {
        cliente_id: cliente.id,
        monto,
        medio_pago: medio,
        comprobante_ref: ref || undefined,
        observaciones: obs || undefined,
      });
    },
    onSuccess: onSaved,
  });

  return (
    <div className={ui.modalOverlay} onClick={onClose}>
      <div className={ui.modalCard} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-slate-900 mb-1">
          Registrar cobro
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          {cliente.razon_social} — saldo actual{" "}
          <span className="font-semibold text-rose-600">
            {money(cliente.saldo_cta_cte)}
          </span>
        </p>
        <div className="space-y-3">
          <div>
            <label className={ui.label}>Monto</label>
            <input
              type="number"
              className={ui.input}
              value={monto}
              onChange={(e) => setMonto(parseFloat(e.target.value) || 0)}
              autoFocus
            />
          </div>
          <div>
            <label className={ui.label}>Medio de pago</label>
            <select
              className={ui.input}
              value={medio}
              onChange={(e) => setMedio(e.target.value)}
            >
              <option>EFECTIVO</option>
              <option>TRANSFERENCIA</option>
              <option>DEBITO</option>
              <option>CREDITO</option>
              <option>QR</option>
            </select>
          </div>
          <div>
            <label className={ui.label}>Comprobante (opcional)</label>
            <input
              className={ui.input}
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </div>
          <div>
            <label className={ui.label}>Observaciones</label>
            <textarea
              className={ui.input}
              rows={2}
              value={obs}
              onChange={(e) => setObs(e.target.value)}
            />
          </div>
        </div>

        {m.isError && (
          <div className={ui.error + " mt-3"}>Error registrando cobro.</div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button className={ui.btnSecondary} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={ui.btnPrimary}
            disabled={m.isPending || monto <= 0}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Procesando..." : "Confirmar cobro"}
          </button>
        </div>
      </div>
    </div>
  );
}
